import { useState } from 'react'
import type { ChallengeKind, OwnerChallenge } from '../../api/ownerTypes'
import { kindLabel, momentToLocalTime, momentToUtc } from './kind'

/** Zoveel tekens past een opdracht; de teller in 22C zegt 60. */
const maxTitle = 60

const kinds: ChallengeKind[] = ['Photo', 'Video', 'Both']

/**
 * Ronde 22C — zelf een challenge maken, en hetzelfde formulier om er een aan
 * te passen.
 *
 * Onderaan staat een voorbeeld van hoe een gast de challenge ziet. Dat is geen
 * versiering: "kort en speels werkt best" is een advies waar je niets aan hebt
 * tot je ziet wat er van jouw zin overblijft op één regel.
 */
export function ChallengeForm({
  open,
  existing,
  eventDateUtc,
  busy,
  onClose,
  onSave,
  onDelete,
}: {
  open: boolean

  /** Null is nieuw; anders bewerken we deze. */
  existing: OwnerChallenge | null
  eventDateUtc: string | null
  busy: boolean
  onClose: () => void
  onSave: (body: { title: string; kind: ChallengeKind; unlocksAtUtc: string | null }) => void
  onDelete?: () => void
}) {
  const [title, setTitle] = useState(existing?.title ?? '')
  const [kind, setKind] = useState<ChallengeKind>(existing?.kind ?? 'Photo')
  const [moment, setMoment] = useState(momentToLocalTime(existing?.unlocksAtUtc ?? null))

  if (!open) {
    return null
  }

  const trimmed = title.trim()

  return (
    <div className="fixed inset-0 z-50 flex lg:items-center lg:justify-center lg:bg-[var(--scrim)] lg:p-8">
      <div className="bg-shell lg:rounded-dialog lg:shadow-dialog flex min-h-full w-full flex-col lg:min-h-0 lg:w-[560px]">
        <header className="flex shrink-0 items-center justify-between px-5.5 pt-5.5 lg:px-7 lg:pt-6">
          <p className="font-display text-[26px] leading-tight lg:text-[30px]">
            {existing ? 'Challenge aanpassen' : 'Nieuwe challenge'}
          </p>

          <button
            type="button"
            aria-label="Sluiten"
            onClick={onClose}
            className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink flex size-9 shrink-0 items-center justify-center rounded-full border transition-colors"
          >
            ✕
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5.5 pt-6 pb-4 lg:px-7">
          <label
            htmlFor="challenge-title"
            className="text-ink-faint mb-2.5 block text-[11px] tracking-[0.18em] uppercase lg:text-xs"
          >
            Opdracht
          </label>
          <input
            id="challenge-title"
            autoFocus
            value={title}
            maxLength={maxTitle}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Iets wat oma zou goedkeuren"
            className="rounded-row border-line-card bg-surface placeholder:text-ink-ghost focus:border-accent w-full border px-4 py-3.75 text-base outline-none"
          />
          <div className="text-ink-faint mt-2 flex justify-between text-[13px]">
            <span>Kort en speels werkt best</span>
            <span>
              {title.length} / {maxTitle}
            </span>
          </div>

          <p className="text-ink-faint mt-6 mb-2.5 text-[11px] tracking-[0.18em] uppercase lg:text-xs">
            Type
          </p>
          <div className="flex gap-2">
            {kinds.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={option === kind}
                onClick={() => setKind(option)}
                className={
                  'h-11.5 flex-1 rounded-full text-[15px] transition-colors ' +
                  (option === kind
                    ? 'bg-ink text-shell font-medium'
                    : 'border-line text-ink-muted hover:bg-tint/50 hover:text-ink border')
                }
              >
                {kindLabel(option)}
              </button>
            ))}
          </div>

          <label
            htmlFor="challenge-moment"
            className="text-ink-faint mt-6 mb-2.5 block text-[11px] tracking-[0.18em] uppercase lg:text-xs"
          >
            Pas zichtbaar vanaf
          </label>
          <input
            id="challenge-moment"
            type="time"
            value={moment}
            onChange={(event) => setMoment(event.target.value)}
            className="rounded-row border-line-card bg-surface focus:border-accent w-full border px-4 py-3.75 text-base outline-none"
          />
          <p className="text-ink-faint mt-2 text-[13px]">Voor iets dat op een moment gebeurt</p>

          {/* --- Voorbeeld ------------------------------------------------ */}
          <p className="text-ink-faint mt-7 mb-2.5 text-[11px] tracking-[0.18em] uppercase lg:text-xs">
            Zo zien gasten het
          </p>
          <div className="rounded-notice border-line-card bg-surface flex items-center gap-3.25 border p-3.5">
            <span className="bg-tint text-accent flex size-9 shrink-0 items-center justify-center rounded-full text-[15px]">
              ◎
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[15px] font-medium">
                {trimmed === '' ? 'Je opdracht komt hier' : trimmed}
              </span>
              <span className="text-ink-faint mt-0.5 block text-[13px]">Nog niemand deed dit</span>
            </span>
          </div>

          {existing && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="text-accent mt-7 text-[15px] hover:underline"
            >
              Deze challenge verwijderen
            </button>
          )}
        </div>

        <footer className="border-line-soft flex shrink-0 gap-2.5 border-t px-5.5 pt-4 pb-7.5 lg:justify-end lg:px-7 lg:py-5">
          <button
            type="button"
            onClick={onClose}
            className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink h-13 shrink-0 basis-29 rounded-full border text-base transition-colors lg:h-12 lg:basis-auto lg:px-6 lg:text-[15px]"
          >
            Annuleren
          </button>

          <button
            type="button"
            disabled={trimmed === '' || busy}
            onClick={() =>
              onSave({ title: trimmed, kind, unlocksAtUtc: momentToUtc(moment, eventDateUtc) })
            }
            className="bg-accent text-accent-ink disabled:bg-inert disabled:text-inert-ink h-13 flex-1 rounded-full text-base font-semibold transition hover:brightness-95 disabled:hover:brightness-100 lg:h-12 lg:flex-none lg:px-7.5 lg:text-[15px]"
          >
            {busy ? 'Bezig…' : existing ? 'Opslaan' : 'Toevoegen'}
          </button>
        </footer>
      </div>
    </div>
  )
}
